import React, { FC, useCallback } from 'react';
// modules
import PriceAlerts from 'modules/PriceAlerts';
import Format from 'modules/Format';
import { useStore } from 'modules/Stores';
import Localization from 'modules/Localization';
// hooks
import useCurrentTheme from 'utils/hooks/useCurrentTheme';
// components
import { Button, CloseIcon, Text, View } from '@upstox/upstox-ui';
// icons
import alertIcon from 'assets/alert_icon.svg';
import alertIconDark from 'assets/alert_icon_dark.svg';
// constants
import { broker } from 'modules/TradingChart/consts';
// typings
import { Props } from './typings';
// styles
import * as css from './styles';

const PriceAlertsNotification: FC<Props> = ({ alert }) => {
  const { isSegmentEnabled, isUserDormantForSegment } = useStore('UserStore');
  const { setChartSymbol } = useStore('TradingChartStore');
  const currentTheme = useCurrentTheme();

  const { id, e: segment, t: token, s: symbol, value, type } = alert;
  const isSegmentAvailable = isSegmentEnabled(segment);
  const isDormantUser = isUserDormantForSegment(segment);
  const isBuySellDisabled = !isSegmentAvailable || isDormantUser;
  const exchangeToken = `${segment}|${token}`;

  const onClose = useCallback(() => PriceAlerts.hideNotification(id), [id]);

  const handleOrder = useCallback((side: number) => {
    if (broker.instance) {
      broker.instance.showOrderDialog({
        symbol: exchangeToken,
        type: 2,
        side,
        qty: 1,
        seenPrice: null,
        isAMO: false,
      });
    }
    onClose();
  }, [exchangeToken, onClose]);

  const openChart = useCallback(() => {
    onClose();
    setChartSymbol(exchangeToken);
  }, [exchangeToken, onClose, setChartSymbol]);

  const renderOpenChartButton = () => (
    <Button
      variant="white"
      css={css.actionButton}
      onClick={openChart}
    >
      <Text weight="bold" color="interactive.primary.default">
        {Localization.translate('OPEN_CHART')}
      </Text>
    </Button>
  );

  const renderActionButtons = () => (
    <View alignItems="center" justifyContent="flex-end" gap={16}>
      <Button variant="white" css={css.actionButton} onClick={() => handleOrder(1)}>
        <Text weight="bold" color="interactive.buy.default">
          {Localization.translate('BUY')}
        </Text>
      </Button>
      <Button variant="white" css={css.actionButton} onClick={() => handleOrder(-1)}>
        <Text weight="bold" color="interactive.sell.active">
          {Localization.translate('SELL')}
        </Text>
      </Button>
    </View>
  );

  return (
    <View padding="medium" css={css.container}>
      <View alignItems="center" justifyContent="space-between">
        <View alignItems="center" gap={8}>
          <img src={currentTheme === 'dark' ? alertIconDark : alertIcon} alt="alert_icon" />
          <Text size="m" family="tvFont" weight="bold">
            {Localization.translate('PRICE_ALERT_NOTIFICATION_TITLE')}
          </Text>
        </View>
        <CloseIcon css={css.closeIcon} onClick={onClose} />
      </View>
      <Text size="m" family="tvFont">
        <Text weight="bold">{symbol || token}</Text>{' '}
        {type === 'greaterthan'
          ? Localization.translate('PRICE_ALERT_GREATER_THAN_TEXT')
          : Localization.translate('PRICE_ALERT_LESS_THAN_TEXT')}
      </Text>
      <Text size="m" weight="bold" family="tvFont">
        {Format.addRupeeSign(Format.formatToIndianFloat(value), true)}
      </Text>
      <View alignItems="center" justifyContent="flex-end" gap={16}>
        {isBuySellDisabled ? renderOpenChartButton() : renderActionButtons()}
      </View>
    </View>
  );
};

export default PriceAlertsNotification;
